import React, { useMemo } from 'react';
import { Arrow, ConnectorPosition, ArrowStyle } from '@/stores/arrowStore';
import { Item } from '@/stores/canvasStore';

interface ArrowPathProps {
  arrow: Arrow;
  items: Item[];
  isSelected: boolean;
  onClick: () => void;
  siblingInfo?: {
    startIndex: number;
    startCount: number;
    endIndex: number;
    endCount: number;
  };
}

// Calculate connector point position on an item
export function getConnectorPoint(
  item: Item,
  position: ConnectorPosition
): { x: number; y: number } {
  const { position_x: x, position_y: y, width, height } = item;
  
  switch (position) {
    case 'top':
      return { x: x + width / 2, y: y };
    case 'bottom':
      return { x: x + width / 2, y: y + height };
    case 'left':
      return { x: x, y: y + height / 2 };
    case 'right':
      return { x: x + width, y: y + height / 2 };
    default:
      return { x: x + width / 2, y: y + height / 2 };
  }
}

// Calculate lateral offset for fan-out when multiple arrows share a connector
function getFanOffset(
  index: number,
  count: number,
  connector: ConnectorPosition | null
): { dx: number; dy: number } {
  if (count <= 1 || !connector) return { dx: 0, dy: 0 };

  // Total spread width scales with count, capped to prevent exceeding item edges
  const SPREAD = Math.min(40, count * 12);
  // Offset from center: evenly distributed from -SPREAD/2 to +SPREAD/2
  const offset = count === 1 ? 0 : ((index - (count - 1) / 2) / (count - 1)) * SPREAD;

  // Offset perpendicular to connector's exit direction
  switch (connector) {
    case 'right':
    case 'left':
      return { dx: 0, dy: offset }; // spread vertically along the edge
    case 'top':
    case 'bottom':
      return { dx: offset, dy: 0 }; // spread horizontally along the edge
    default:
      return { dx: 0, dy: 0 };
  }
}

// Calculate control points for bezier curve
function calculateControlPoints(
  start: { x: number; y: number },
  end: { x: number; y: number },
  startConnector: ConnectorPosition | null,
  endConnector: ConnectorPosition | null
) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const offset = Math.min(distance * 0.4, 100);
  
  let cp1 = { x: start.x, y: start.y };
  let cp2 = { x: end.x, y: end.y };
  
  // Adjust control points based on connector positions
  if (startConnector) {
    switch (startConnector) {
      case 'top':
        cp1 = { x: start.x, y: start.y - offset };
        break;
      case 'bottom':
        cp1 = { x: start.x, y: start.y + offset };
        break;
      case 'left':
        cp1 = { x: start.x - offset, y: start.y };
        break;
      case 'right':
        cp1 = { x: start.x + offset, y: start.y };
        break;
    }
  } else {
    // Free-floating start - use direction
    cp1 = { x: start.x + dx * 0.3, y: start.y + dy * 0.3 };
  }
  
  if (endConnector) {
    switch (endConnector) {
      case 'top':
        cp2 = { x: end.x, y: end.y - offset };
        break;
      case 'bottom':
        cp2 = { x: end.x, y: end.y + offset };
        break;
      case 'left':
        cp2 = { x: end.x - offset, y: end.y };
        break;
      case 'right':
        cp2 = { x: end.x + offset, y: end.y };
        break;
    }
  } else {
    // Free-floating end - use direction
    cp2 = { x: end.x - dx * 0.3, y: end.y - dy * 0.3 };
  }
  
  return { cp1, cp2 };
}

// Generate SVG path for 'straight' style
function generateStraightPath(
  start: { x: number; y: number },
  end: { x: number; y: number }
): string {
  return `M ${start.x} ${start.y} L ${end.x} ${end.y}`;
}

// Generate SVG path for 'step' style (right-angle path)
function generateStepPath(
  start: { x: number; y: number },
  end: { x: number; y: number },
  startConnector: ConnectorPosition | null,
  endConnector: ConnectorPosition | null
): string {
  // Determine routing based on connectors
  const isHorizontalStart = startConnector === 'left' || startConnector === 'right';
  const isHorizontalEnd = endConnector === 'left' || endConnector === 'right';

  if (isHorizontalStart && isHorizontalEnd) {
    // Both horizontal: route with horizontal-vertical-horizontal
    const midX = (start.x + end.x) / 2;
    return `M ${start.x} ${start.y} L ${midX} ${start.y} L ${midX} ${end.y} L ${end.x} ${end.y}`;
  } else if (!isHorizontalStart && !isHorizontalEnd) {
    // Both vertical: route with vertical-horizontal-vertical
    const midY = (start.y + end.y) / 2;
    return `M ${start.x} ${start.y} L ${start.x} ${midY} L ${end.x} ${midY} L ${end.x} ${end.y}`;
  } else if (isHorizontalStart) {
    // Start horizontal, end vertical
    return `M ${start.x} ${start.y} L ${end.x} ${start.y} L ${end.x} ${end.y}`;
  } else {
    // Start vertical, end horizontal
    return `M ${start.x} ${start.y} L ${start.x} ${end.y} L ${end.x} ${end.y}`;
  }
}

// Calculate angle for arrowhead rotation
function calculateAngle(fromX: number, fromY: number, toX: number, toY: number): number {
  return Math.atan2(toY - fromY, toX - fromX) * (180 / Math.PI);
}

export function ArrowPath({ arrow, items, isSelected, onClick, siblingInfo }: ArrowPathProps) {
  // Extract connected items outside useMemo so their positions can be explicit dependencies
  const startItem = arrow.start_item_id ? items.find(i => i.id === arrow.start_item_id) : null;
  const endItem = arrow.end_item_id ? items.find(i => i.id === arrow.end_item_id) : null;

  const pathData = useMemo(() => {
    // Get start point
    let start: { x: number; y: number };
    if (arrow.start_item_id && arrow.start_connector && startItem) {
      start = getConnectorPoint(startItem, arrow.start_connector);
      // Apply fan offset for sibling arrows on the same connector
      if (siblingInfo && siblingInfo.startCount > 1) {
        const fan = getFanOffset(siblingInfo.startIndex, siblingInfo.startCount, arrow.start_connector);
        start = { x: start.x + fan.dx, y: start.y + fan.dy };
      }
    } else if (arrow.start_x !== null && arrow.start_y !== null) {
      start = { x: arrow.start_x, y: arrow.start_y };
    } else {
      return null;
    }

    // Get end point
    let end: { x: number; y: number };
    if (arrow.end_item_id && arrow.end_connector && endItem) {
      end = getConnectorPoint(endItem, arrow.end_connector);
      // Apply fan offset for sibling arrows on the same connector
      if (siblingInfo && siblingInfo.endCount > 1) {
        const fan = getFanOffset(siblingInfo.endIndex, siblingInfo.endCount, arrow.end_connector);
        end = { x: end.x + fan.dx, y: end.y + fan.dy };
      }
    } else if (arrow.end_x !== null && arrow.end_y !== null) {
      end = { x: arrow.end_x, y: arrow.end_y };
    } else {
      return null;
    }

    const style: ArrowStyle = arrow.style || 'bezier';
    let path: string;
    let cp1: { x: number; y: number };
    let cp2: { x: number; y: number };

    if (style === 'straight') {
      path = generateStraightPath(start, end);
      // For arrowhead angles on straight lines
      cp1 = start;
      cp2 = end;
    } else if (style === 'step') {
      path = generateStepPath(start, end, arrow.start_connector, arrow.end_connector);
      // For step paths, arrowhead should point in the final segment direction
      // Parse the last two points from the path for endAngle
      cp1 = start;
      cp2 = end;
    } else {
      // bezier (default)
      const controlPoints = calculateControlPoints(
        start,
        end,
        arrow.start_connector,
        arrow.end_connector
      );
      cp1 = controlPoints.cp1;
      cp2 = controlPoints.cp2;
      path = `M ${start.x} ${start.y} C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${end.x} ${end.y}`;
    }

    // Calculate angles for arrowheads
    const startAngle = calculateAngle(cp1.x, cp1.y, start.x, start.y);
    const endAngle = calculateAngle(cp2.x, cp2.y, end.x, end.y);

    return {
      start,
      end,
      cp1,
      cp2,
      startAngle,
      endAngle,
      path,
      midpoint: { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 },
    };
  }, [arrow, startItem?.position_x, startItem?.position_y, startItem?.width, startItem?.height, endItem?.position_x, endItem?.position_y, endItem?.width, endItem?.height, siblingInfo]);
  
  if (!pathData) return null;

  const { start, end, path, startAngle, endAngle, midpoint } = pathData;
  const strokeColor = arrow.stroke_color || '#374151';
  const strokeWidth = arrow.stroke_width ?? 2;
  const isAnimated = arrow.animated ?? false;

  return (
    <g className="arrow-path" onClick={onClick} style={{ cursor: 'pointer' }}>
      {/* Invisible wider path for easier clicking */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        style={{ cursor: 'pointer' }}
      />

      {/* Selection highlight glow */}
      {isSelected && (
        <path
          d={path}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth={strokeWidth + 6}
          opacity={0.15}
          strokeLinecap="round"
        />
      )}

      {/* Visible path */}
      <path
        d={path}
        fill="none"
        stroke={isSelected ? 'hsl(var(--primary))' : strokeColor}
        strokeWidth={isSelected ? strokeWidth + 0.5 : strokeWidth}
        strokeLinecap="round"
        strokeDasharray={isAnimated ? '8 4' : 'none'}
        className={isAnimated ? 'animate-dash' : ''}
      />

      {/* Start arrowhead */}
      {arrow.start_arrowhead && (
        <polygon
          points="-8,-5 0,0 -8,5"
          fill={isSelected ? 'hsl(var(--primary))' : strokeColor}
          transform={`translate(${start.x}, ${start.y}) rotate(${startAngle})`}
        />
      )}

      {/* End arrowhead */}
      {arrow.end_arrowhead && (
        <polygon
          points="-8,-5 0,0 -8,5"
          fill={isSelected ? 'hsl(var(--primary))' : strokeColor}
          transform={`translate(${end.x}, ${end.y}) rotate(${endAngle})`}
        />
      )}

      {/* Label at midpoint */}
      {arrow.label && (
        <g>
          <rect
            x={midpoint.x - (arrow.label.length * 3.5) - 4}
            y={midpoint.y - 18}
            width={arrow.label.length * 7 + 8}
            height={20}
            rx={4}
            fill="white"
            opacity={0.9}
          />
          <text
            x={midpoint.x}
            y={midpoint.y - 5}
            textAnchor="middle"
            className="select-none"
            style={{ fontSize: '11px', fill: '#64748b' }}
          >
            {arrow.label}
          </text>
        </g>
      )}

      {/* Selection handles */}
      {isSelected && (
        <>
          <circle
            cx={start.x}
            cy={start.y}
            r={6}
            fill="hsl(var(--background))"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            className="cursor-move"
          />
          <circle
            cx={end.x}
            cy={end.y}
            r={6}
            fill="hsl(var(--background))"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            className="cursor-move"
          />
        </>
      )}
    </g>
  );
}
